
import React from 'react';
import { Database, Users, FileText, Hospital } from 'lucide-react';

const KeyRequirements: React.FC = () => {
  return (
    <>
      <h2 className="text-2xl font-bold mb-4 mt-8 text-itmgmt-dark-blue">Key IT Requirements for HIPAA Compliance</h2>
      <p className="mb-4 text-gray-700 leading-relaxed">
        While HIPAA doesn't specify exact technologies to implement, it outlines clear requirements that your IT infrastructure must meet:
      </p>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue flex items-center">
        <Database className="w-5 h-5 mr-2" />
        1. Data Encryption
      </h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        All ePHI must be encrypted both at rest (stored data) and in transit (data being transferred). This is considered an "addressable" requirement, meaning you must implement it unless there's a justifiable reason not to.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Implementation tip:</strong> At minimum, implement:
        </p>
        <ul className="list-disc ml-6 italic text-gray-700">
          <li>256-bit AES encryption for stored data</li>
          <li>TLS 1.2 or higher for data transmission</li>
          <li>End-to-end encryption for emails containing PHI</li>
        </ul>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue flex items-center">
        <Users className="w-5 h-5 mr-2" />
        2. Access Controls
      </h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        Your systems must limit PHI access to only those employees who need it for their job functions (the principle of least privilege). This requires technical capabilities to assign specific access rights.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Implementation tip:</strong> Your access control system should include:
        </p>
        <ul className="list-disc ml-6 italic text-gray-700">
          <li>Unique user identification (no shared logins)</li>
          <li>Role-based access controls</li>
          <li>Automatic logoff after periods of inactivity</li>
          <li>Multi-factor authentication for remote access</li>
        </ul>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue flex items-center">
        <FileText className="w-5 h-5 mr-2" />
        3. Audit Controls
      </h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        HIPAA requires mechanisms to record and examine activity in systems that contain or use ePHI. These audit trails help identify inappropriate access and serve as evidence during compliance reviews.
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Implementation tip:</strong> Your audit system should:
        </p>
        <ul className="list-disc ml-6 italic text-gray-700">
          <li>Log all access to PHI (successful and failed attempts)</li>
          <li>Track user activities, including file modifications and exports</li>
          <li>Include timestamps and user identifiers</li>
          <li>Store logs securely with retention policies aligned with your overall record retention policies</li>
        </ul>
      </div>

      <h3 className="text-xl font-semibold mb-3 mt-6 text-itmgmt-medium-blue flex items-center">
        <Hospital className="w-5 h-5 mr-2" />
        4. Business Continuity and Disaster Recovery
      </h3>
      <p className="mb-3 text-gray-700 leading-relaxed">
        You must have capabilities to recover access to ePHI during emergencies. This includes both technical disasters (system failures) and physical disasters (natural disasters, facility damage).
      </p>
      <div className="mb-6 pl-5 border-l-4 border-itmgmt-light-blue">
        <p className="italic text-gray-700">
          <strong>Implementation tip:</strong> Your disaster recovery strategy should include:
        </p>
        <ul className="list-disc ml-6 italic text-gray-700">
          <li>Regular, automated backups with encryption</li>
          <li>Offsite storage of backups</li>
          <li>Documented recovery procedures with assigned responsibilities</li>
          <li>Testing of recovery capabilities at least annually</li>
          <li>Redundant systems for critical applications</li>
        </ul>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg mb-8">
        <h3 className="text-xl font-semibold mb-3 text-itmgmt-dark-blue">The Cost of Non-Compliance</h3>
        <p className="text-gray-700 mb-3">
          HIPAA violations can result in substantial penalties:
        </p>
        <ul className="list-disc ml-6 mb-3 text-gray-700">
          <li className="mb-2"><strong>Tier 1:</strong> $100-$50,000 per violation (unknowing)</li>
          <li className="mb-2"><strong>Tier 2:</strong> $1,000-$50,000 per violation (reasonable cause)</li>
          <li className="mb-2"><strong>Tier 3:</strong> $10,000-$50,000 per violation (willful neglect, corrected)</li>
          <li className="mb-2"><strong>Tier 4:</strong> $50,000+ per violation (willful neglect, not corrected)</li>
          <li className="mb-2"><strong>Maximum annual penalty:</strong> $1.5 million per violation category</li>
        </ul>
        <p className="text-gray-700">
          Beyond financial penalties, breaches damage patient trust and your practice's reputation.
        </p>
      </div>
    </>
  );
};

export default KeyRequirements;
